/* Схема шкафа — вид спереди.
   Рама и стойки с отверстиями, номера юнитов на левой стойке.
   Устройства — лицевые панели с «ушами» на винтах; табличка справа.
   Все цвета — токены --rack-* и --dev-* (светлая и тёмная тема). */

.rack {
  container-type: inline-size;
  overflow: hidden;
  background: var(--rack-rail);
  border: 1px solid var(--rack-frame);
  border-radius: var(--r-md);
}

/* Верх и низ шкафа */
.cap {
  height: 10px;
  background: var(--rack-frame);
}

/* ── Ряд: номера | стойка | устройство | стойка ── */
.row {
  display: grid;
  grid-template-columns: 30px 14px minmax(0, 1fr) 14px;
  background: var(--rack-inside);
}

.numbers {
  display: flex;
  flex-direction: column;
  background: var(--rack-rail);
}

.numbers span {
  display: grid;
  flex: 1;
  place-items: center;
  font-family: var(--mono);
  font-size: 11px;
  color: var(--text2);
  font-variant-numeric: tabular-nums;
}

/* Стойка: три квадратных отверстия на юнит, как на настоящей рейке. */
.rail {
  background:
    repeating-linear-gradient(
        to bottom,
        transparent 0 2.5px,
        var(--rack-hole) 2.5px 8.5px,
        transparent 8.5px calc(34px / 3)
      )
      center top / 6px 100% no-repeat,
    var(--rack-rail);
}

/* ── Устройство ── */
.device {
  --kind: var(--slate-lt);
  display: flex;
  align-items: stretch;
  min-width: 0;
  margin: 1px 0;
  color: var(--text);
  background: var(--dev-body);
  border: 1px solid var(--dev-edge);
  border-radius: 2px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition:
    filter 0.12s,
    border-color 0.12s;
}

:global(html.dark) .device {
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.device:hover {
  filter: brightness(0.97);
}
:global(html.dark) .device:hover {
  filter: brightness(1.18);
}

.device:focus-visible {
  outline: 2px solid var(--blue);
  outline-offset: -1px;
}

/* Цвет типа — только метка на табличке; сам тип подписан словом. */
.kind_switch {
  --kind: var(--blue);
}
.kind_patch {
  --kind: var(--ok);
}
.kind_optic {
  --kind: var(--orange);
}
.kind_organizer {
  --kind: var(--slate-lt);
}
.kind_ups {
  --kind: var(--warn);
}
.kind_server {
  --kind: var(--purple);
}
.kind_shelf {
  --kind: var(--slate-lt);
}
.kind_other {
  --kind: var(--purple);
}

/* «Уши» с винтами — по винту на юнит */
.ear {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 14px;
  background: color-mix(in srgb, var(--dev-edge) 45%, var(--dev-body));
}

.ear i {
  flex: 1;
  background: radial-gradient(circle, var(--dev-screw) 0 2.4px, transparent 2.9px);
}

/* ── Лицевая панель ── */
.art {
  display: flex;
  flex: 0 1 42%;
  align-items: center;
  max-width: 300px;
  gap: 6px;
  min-width: 0;
  padding: 4px 8px;
  overflow: hidden;
}

/* Коммутатор: два ряда RJ45 с индикаторами и два SFP */
.portGrid {
  display: grid;
  flex: 1;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-auto-rows: 10px;
  gap: 2px;
  align-content: center;
  min-width: 0;
}

.rj45 {
  position: relative;
  background: var(--dev-hole);
  border-radius: 1px;
}

.rj45 i {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 3px;
  height: 2px;
  border-radius: 1px;
}

.ledOn {
  background: var(--led-clean);
}
.ledOff {
  background: color-mix(in srgb, var(--dev-screw) 50%, transparent);
}

.sfps {
  display: flex;
  gap: 3px;
  flex-shrink: 0;
}

.sfp {
  width: 14px;
  height: 10px;
  background: var(--dev-hole);
  border: 1px solid var(--dev-screw);
  border-radius: 1px;
}

/* Патч-панель: ряд белых гнёзд */
.artPatch {
  display: grid;
  grid-template-columns: repeat(24, minmax(0, 1fr));
  grid-auto-rows: 12px;
  gap: 2px;
  align-content: center;
  align-items: stretch;
}

.keystone {
  position: relative;
  background: var(--dev-key);
  border: 1px solid var(--dev-edge);
  border-radius: 1px;
}

.keystone::after {
  content: '';
  position: absolute;
  inset: 2px 1px 3px;
  background: var(--dev-hole);
  border-radius: 1px;
}

/* Оптика: пары LC */
.artOptic {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-auto-rows: 12px;
  gap: 3px;
  align-content: center;
  align-items: stretch;
}

.lc {
  display: flex;
  gap: 1px;
  justify-content: center;
  padding: 2px 1px;
  background: var(--dev-hole);
  border-radius: 1px;
}

.lc i {
  flex: 0 1 3px;
  background: var(--dev-fiber);
  border-radius: 1px;
}

/* Органайзер: прорези под кабель */
.artOrganizer {
  gap: 8px;
}

.ring {
  flex: 1;
  height: 9px;
  background: var(--dev-hole);
  border-radius: 5px;
}

/* ИБП: экран, кнопки, решётка */
.lcd {
  flex-shrink: 0;
  padding: 1px 6px;
  font-family: var(--mono);
  font-size: 10px;
  line-height: 14px;
  color: var(--dev-lcd-text);
  background: var(--dev-lcd);
  border-radius: 2px;
}

.buttons {
  display: flex;
  gap: 3px;
  flex-shrink: 0;
}

.buttons span {
  width: 6px;
  height: 6px;
  background: var(--dev-screw);
  border-radius: 50%;
}

.grille {
  flex: 1;
  align-self: stretch;
  margin: 2px 0;
  background: repeating-linear-gradient(90deg, var(--dev-hole) 0 2px, transparent 2px 5px);
  opacity: 0.55;
}

/* Сервер: корзины дисков и кнопка питания */
.bays {
  display: grid;
  flex: 1;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-auto-rows: 10px;
  gap: 2px;
  align-content: center;
}

.bay {
  position: relative;
  background: color-mix(in srgb, var(--dev-hole) 25%, var(--dev-body));
  border: 1px solid var(--dev-edge);
  border-radius: 1px;
}

.bay i {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 3px;
  height: 3px;
  background: var(--led-clean);
  border-radius: 50%;
}

.power {
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  border: 1.5px solid var(--led-clean);
  border-radius: 50%;
}

/* Полка: лоток снизу */
.artShelf {
  align-items: flex-end;
}

.tray {
  flex: 1;
  height: 4px;
  background: var(--dev-edge);
  border-radius: 1px;
}

/* Прочее: вентиляционные прорези */
.vents {
  flex: 1;
  height: 8px;
  background: repeating-linear-gradient(90deg, var(--dev-edge) 0 7px, transparent 7px 10px);
  border-radius: 1px;
}

/* ── Табличка с названием ── */
.plate {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin: 4px 0;
  padding: 0 8px 0 10px;
  background: var(--dev-label);
  border: 1px solid var(--dev-edge);
  border-radius: 2px;
  box-shadow: inset 3px 0 0 var(--kind);
}

.plateMain {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.name {
  overflow: hidden;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.comment {
  overflow: hidden;
  font-size: 12px;
  color: var(--text2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tags {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
}

.badge {
  font-size: 11.5px;
  color: var(--text2);
  white-space: nowrap;
}

.badgeErr {
  padding: 0 6px;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 18px;
  color: var(--err);
  white-space: nowrap;
  background: var(--err-bg);
  border: 1px solid color-mix(in srgb, var(--err) 40%, transparent);
  border-radius: var(--r-pill);
}

.open {
  height: 22px;
  padding: 0 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--blue);
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  cursor: pointer;
}

.open:hover {
  border-color: var(--blue);
}
:global(html.dark) .open {
  color: var(--navy-accent);
}

/* ── Резерв: место занято на бумаге, оборудования нет ── */
.kind_reserve {
  background: transparent;
  border-style: dashed;
  box-shadow: none;
}

.kind_reserve .ear {
  background: transparent;
}
.kind_reserve .plate {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
}
.kind_reserve .name {
  font-weight: 400;
  color: var(--text2);
}

/* ── Расхождение с учётом важнее типа ── */
.mismatch {
  --kind: var(--err);
  border-color: var(--err);
  box-shadow: 0 0 0 1px var(--err);
}

.mismatch .plate {
  background: var(--err-bg);
}

/* ── Свободный юнит: видна стойка изнутри ── */
.rowEmpty .device {
  justify-content: center;
  align-items: center;
  background: transparent;
  border: 1px dashed transparent;
  box-shadow: none;
}

.rowEmpty .device:hover {
  filter: none;
  border-color: var(--blue);
}

/* Подпись появляется при наведении: сорок строк «свободно» подряд
   только шумят, а пустую стойку и так видно. */
.free {
  font-size: 12px;
  color: var(--blue);
  opacity: 0;
}

.rowEmpty .device:hover .free,
.rowEmpty .device:focus-visible .free {
  opacity: 1;
}

:global(html.dark) .free {
  color: var(--navy-accent);
}

/* ── Узкая схема: рисунок уже, подпись типа прячется ── */
@container (max-width: 640px) {
  .badge {
    display: none;
  }
}

/* Имя на табличке важнее рисунка: рисунок уступает место первым. */
@container (max-width: 540px) {
  .art {
    flex-basis: 28%;
    padding: 4px 6px;
  }
  .plate {
    gap: 6px;
    padding: 0 6px 0 8px;
  }
  .open {
    padding: 0 6px;
  }
  .badge,
  .comment {
    display: none;
  }
  .sfps {
    display: none;
  }
}

@container (max-width: 400px) {
  .row {
    grid-template-columns: 24px 10px minmax(0, 1fr) 10px;
  }
  .rail {
    background-size:
      4px 100%,
      auto;
  }
  .ear {
    width: 9px;
  }
  .art {
    flex-basis: 24%;
    padding: 4px;
  }
  .open {
    font-size: 11.5px;
  }
  .lcd,
  .buttons {
    display: none;
  }
  .name {
    font-size: 12px;
  }
}
